import React, { useCallback, useEffect, useState } from 'react'
import * as Yup from "yup";
import { validateNoStartOrEndSpace } from '../../cutomValidationField';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showErrorMessage, showPromiseNotification } from '../../common/notification';
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import { checkUniqueUser, resendOtp, setUserInfo, signupUser } from '../../slices/authSlice';
import { decryption, encryption } from '../../common/enc_dec';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { GoogleAuthProvider, signInWithPopup, OAuthProvider } from 'firebase/auth';
import { auth, firebaseMessaging } from '../../firebase/firebaseConfig';
import { useTranslation } from 'react-i18next';
import '../../i18';
import { getLocalStorageItem } from '../../common/LocalStorageService';
import { getToken } from 'firebase/messaging';

const Signup = () => {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);
    const [profileData, setProfileData] = useState({ login_type: 'S' });
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [phone, setPhone] = useState('91');
    const [deviceToken, setDeviceToken] = useState("");
    const [searchParams] = useSearchParams();

    // Decode data from URL query params if coming from signin
    useEffect(() => {
        const dataParam = searchParams.get('data');
        if (dataParam) {
            try {
                const decodedData = decryption(decodeURIComponent(dataParam), true);
                if (decodedData) {
                    // Set profile data based on login type
                    setProfileData(decodedData);
                    
                    // Pre-fill form fields
                    if (decodedData.name) {
                        setValue('full_name', decodedData.name);
                    }
                    if (decodedData.email) {
                        setValue('email', decodedData.email);
                    }
                    if (decodedData.phone_number) {
                        setValue('phone_number', decodedData.phone_number);
                    }
                    if (decodedData.country_code) {
                        setPhone(decodedData.country_code);
                    }
                }
            } catch (error) {
                console.error('Error decoding signup data:', error);
            }
        }
    }, [searchParams]);

    const createSignupSchema = (loginType) => {
        return Yup.object().shape({
            full_name: Yup.string()
                .required(t('required', { field: t("name") }))
                .test('no-start-or-end-space', t('noStartEndSpace', { field: t("Full_Name") }), validateNoStartOrEndSpace)
                .min(2, t('min', { field: t("Full_Name"), value: 2 }))
                .max(50, t('max', { field: t("Full_Name"), value: 50 }))
                .matches(/^[a-zA-Z\s'-]*$/, t('fullNameValidation', { field: t("Full_Name") })),
            email: Yup.string().trim().required(t('required', { field: t("email") })).email(t('valid', { field: t("email") })),
            password: profileData?.login_type === 'S' ?
                Yup.string().trim().required(t('required', { field: t("password") })).matches(
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                    t('passwordValidation')
                )
                : Yup.string(),
            phone_number: Yup.string().trim()
                .test('phone-validation', t('valid', { field: t("phone_number") }), function (value) {
                    // If no value provided, it's valid (optional field)
                    if (!value || value.trim() === '') {
                        return true;
                    }
                    // If value provided, validate length (exactly 10 digits)
                    if (value.length !== 10) {
                        return this.createError({
                            message: t('phoneLength', { field: t("phone_number"), length: 10 }) || 'Phone number must be exactly 10 digits'
                        });
                    }
                    // Validate format (only digits)
                    if (!/^\d{10}$/.test(value)) {
                        return this.createError({
                            message: t('phoneFormat', { field: t("phone_number") }) || 'Phone number must contain only digits'
                        });
                    }
                    return true;
                }),
            terms_and_conditions: Yup.bool()
                .oneOf([true], t('tNcValidation'))
        })
    }

    const signupSchema = createSignupSchema(profileData?.login_type);
    const formOptions = { resolver: yupResolver(signupSchema) };
    const { register, handleSubmit, formState, setValue } = useForm(formOptions);
    const { errors } = formState;

    const language = getLocalStorageItem("language")

    // Initialize Facebook SDK
    useEffect(() => {
        // Load Facebook SDK
        const loadFacebookSDK = () => {
            return new Promise((resolve) => {
                if (window.FB) {
                    resolve();
                    return;
                }

                window.fbAsyncInit = function () {
                    window.FB.init({
                        appId: process.env.REACT_APP_FACEBOOK_APP_ID,
                        cookie: true,
                        xfbml: true,
                        version: 'v19.0'
                    });
                    resolve();
                };

                // Load Facebook SDK script
                const script = document.createElement('script');
                script.src = 'https://connect.facebook.net/en_US/sdk.js';
                script.async = true;
                script.defer = true;
                script.crossOrigin = 'anonymous';
                document.head.appendChild(script);
            });
        };

        loadFacebookSDK();
    }, []);

    useEffect(() => {
        const fetchDeviceToken = async () => {
            try {
                const permission = await Notification.requestPermission();
                if (permission === "granted") {
                    const token = await getToken(firebaseMessaging, {
                        vapidKey: process.env.REACT_APP_VAPID_KEY,
                        serviceWorkerRegistration: await navigator.serviceWorker.register('/firebase-messaging-sw.js')
                    });
                    if (token) {
                        setDeviceToken(token);
                    } else {
                        console.warn("No device token retrieved. Ensure your app is correctly configured.");
                    }
                } else {
                    console.warn("Notification permission denied.");
                }
            } catch (err) {
                console.error("Error retrieving device token:", err);
            }
        };

        fetchDeviceToken();
    }, []);

    const onSubmit = useCallback(
        (values) => {
            if (profileData.login_type === 'S' && values.phone_number && values.phone_number.trim()) {
                const phoneValue = values.phone_number.trim();

                // Check if phone number is exactly 10 digits
                if (phoneValue.length !== 10) {
                    showErrorMessage(t('phoneLength', { field: t("phone_number"), length: 10 }) || 'Phone number must be exactly 10 digits');
                    return;
                }

                // Check if phone number contains only digits
                if (!/^\d{10}$/.test(phoneValue)) {
                    showErrorMessage(t('phoneFormat', { field: t("phone_number") }) || 'Phone number must contain only digits');
                    return;
                }
            }
            const submissionData = {
                language_id: language?.lang_id || 1,
                full_name: values.full_name,
                email: values.email,
                terms_and_conditions: values.terms_and_conditions,
                device_token: deviceToken,
                device_type: "W"
            }
            if (profileData.login_type !== 'S') {
                submissionData.login_type = profileData.login_type;
                submissionData.social_id = profileData.social_id;
            }
            else {
                submissionData.login_type = 'S';
                submissionData.password = values.password;
                submissionData.email = values.email;
                if (values.phone_number && values.phone_number.trim()) {
                    submissionData.country_code = phone;
                    submissionData.phone_number = values.phone_number;
                }
            }
            if (submissionData.login_type === 'S') {
                dispatch(setUserInfo(submissionData));
                const uniqueCheckPayload = {
                    email: submissionData.email
                };

                if (submissionData.phone_number) {
                    uniqueCheckPayload.country_code = submissionData.country_code;
                    uniqueCheckPayload.phone_number = submissionData.phone_number;
                }
                dispatch(checkUniqueUser({
                    payload: uniqueCheckPayload
                }))
                    .then((checkUniqueResponse) => {
                        if (checkUniqueResponse?.payload?.code == 200) {
                            if (submissionData.phone_number || submissionData.email) {
                                const myPromise = new Promise((resolve, reject) => {
                                    dispatch(
                                        resendOtp({
                                            payload: {
                                                is_forget: false,
                                                country_code: submissionData.country_code,
                                                phone_number: submissionData.phone_number,
                                                email: submissionData.email
                                            },
                                        })
                                    )
                                        .then((response) => {
                                            if (response.payload.code === 200) {
                                                resolve(response.payload);
                                                localStorage.setItem("otpTimer", btoa("60"));
                                                localStorage.setItem("otpGenerateTime", btoa(response.payload.data.generate_time))
                                                const params = {}
                                                params.is_forget = false;
                                                params.country_code = response.payload.data.country_code;
                                                params.phone_number = response.payload.data.phone_number;
                                                params.session_id = response.payload.data.session_id;
                                                params.email = response.payload.data.email;
                                                const queryString = encryption(params, true);
                                                navigate(`/otp-verification?data=${encodeURIComponent(queryString)}`);
                                            }
                                            else {
                                                reject(response.payload)
                                            }
                                        })

                                });

                                showPromiseNotification(myPromise);
                            }
                            else {
                                const signupPromise = new Promise((signupResolve, signupReject) => {
                                    dispatch(
                                        signupUser({
                                            payload: {
                                                ...submissionData
                                            },
                                        })
                                    )
                                        .then((signupResponse) => {
                                            if (signupResponse.payload.code === 200) {
                                                signupResolve(signupResponse.payload);
                                                navigate("/complete-profile");
                                            } else {
                                                signupReject(signupResponse.payload);
                                            }
                                        });
                                });
                                showPromiseNotification(signupPromise);
                            }
                        }
                        else {
                            showErrorMessage(checkUniqueResponse?.payload?.message)
                        }
                    })
            }
            else {
                const signupPromise = new Promise((signupResolve, signupReject) => {
                    dispatch(
                        signupUser({
                            payload: {
                                ...submissionData
                            },
                        })
                    )
                        .then((signupResponse) => {
                            if (signupResponse.payload.code === 200) {
                                signupResolve(signupResponse.payload);
                                navigate("/complete-profile");
                            } else {
                                signupReject(signupResponse.payload);
                            }
                        });
                });
                showPromiseNotification(signupPromise);
            }
        },
        [dispatch, navigate, phone, profileData, language, deviceToken]
    );

    const handleGoogleSignIn = async (e) => {
        try {
            const provider = new GoogleAuthProvider();
            await signInWithPopup(auth, provider);
            let data = {}
            data.name = auth.currentUser.displayName;
            data.email = auth.currentUser.email;
            data.social_id = auth.currentUser.uid;
            data.login_type = 'G';
            setValue("full_name", data.name);
            setValue("email", data.email);
            setProfileData(data);
        }
        catch (err) {
            if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') return;
            showErrorMessage(t('errorMsg'));
        }
    }

    const handleFacebookSignIn = async (e) => {
        try {
            if (!window.FB) {
                showErrorMessage('Facebook SDK not loaded');
                return;
            }

            // Check login status first
            window.FB.getLoginStatus((response) => {
                if (response.status === 'connected') {
                    // User is already logged in, get their info
                    getFacebookUserInfo(response.authResponse.accessToken);
                } else {
                    // User needs to login
                    window.FB.login((loginResponse) => {
                        if (loginResponse.authResponse) {
                            getFacebookUserInfo(loginResponse.authResponse.accessToken);
                        } else {
                            console.log('User cancelled login or did not fully authorize.');
                        }
                    }, {
                        scope: 'email,public_profile'
                    });
                }
            });

        } catch (err) {
            console.error('Facebook login error:', err);
            showErrorMessage(t('errorMsg'));
        }
    }

    // Get Facebook user information
    const getFacebookUserInfo = (accessToken) => {
        window.FB.api('/me', {
            fields: 'id,name,email,picture.type(large)'
        }, (response) => {
            if (response && !response.error) {
                let data = {
                    name: response.name,
                    email: response.email,
                    social_id: response.id,
                    login_type: 'F',
                    picture: response.picture?.data?.url || null,
                    access_token: accessToken
                };

                setValue("full_name", data.name);
                setValue("email", data.email);
                setProfileData(data);
            } else {
                console.error('Error getting user info:', response.error);
                showErrorMessage('Failed to get user information from Facebook');
            }
        });
    }

    const handleAppleSignIn = async (e) => {
        try {
            const provider = new OAuthProvider('apple.com');
            await signInWithPopup(auth, provider);
            let data = {}
            data.name = auth.currentUser.displayName;
            data.email = auth.currentUser.email;
            data.social_id = auth.currentUser.uid;
            data.login_type = 'A';
            setValue("full_name", data.name);
            setValue("email", data.email);
            setProfileData(data);
        }
        catch (err) {
            if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') return;
            showErrorMessage("Something went wrong! Please try again.");
        }
    }

    return (
        <div className="signin-page-area pb-4 h-100" style={{ paddingTop: "100px" }}>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-xl-6 col-lg-7">
                        <form className="signin-inner" onSubmit={handleSubmit(onSubmit)}>
                            <div className="row">
                                <div className="col-12">
                                    <div className="single-input-inner style-bg-border">
                                        <h4 className="SignInUpHeading">
                                            {t('signUp')}
                                        </h4>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="single-input-inner style-bg-border">
                                        <input defaultValue={profileData ? profileData?.name : ''} name="full_name" type="text" placeholder={t('enterFieldName', { field: t("Name") })} {...register('full_name')} />
                                        <p className="text-danger">
                                            {errors.full_name?.message}
                                        </p>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="single-input-inner style-bg-border">
                                        <input name="email" defaultValue={profileData ? profileData?.email : ''} type="text" placeholder={t('enterFieldName', { field: t("Email") })} {...register('email')} />
                                        <p className="text-danger">
                                            {errors.email?.message}
                                        </p>
                                    </div>
                                </div>
                                {
                                    profileData.login_type === 'S' && (
                                        <div className="col-12">
                                            <div className="single-input-inner style-bg-border">
                                                <div className="single-input-inner style-bg-border" style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                                                    <div style={{ display: 'flex', gap: '5px' }}>
                                                        <PhoneInput
                                                            country={'in'}
                                                            value={phone}
                                                            onChange={(phone, data) => {
                                                                setPhone(data.dialCode)
                                                            }}
                                                            inputProps={{
                                                                name: 'phone',
                                                                autoFocus: true,
                                                                disabled: true
                                                            }}
                                                            containerStyle={{ width: '150px', height: '50px' }}
                                                            inputStyle={{ width: '100%', height: '100%' }}
                                                        />
                                                        <input
                                                            type="number"
                                                            placeholder={t('phoneOptional')}
                                                            name='phone_number'
                                                            {...register('phone_number')}
                                                            onChange={(e) => {
                                                                const inputValue = e.target.value;
                                                                if (inputValue.length > 10) {
                                                                    e.target.value = inputValue.slice(0, 10);
                                                                }

                                                                if (inputValue.length <= 10) {
                                                                    e.target.value = inputValue;
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <p className="text-danger" style={{ display: 'block', marginTop: '5px' }}>
                                                        {errors.phone_number?.message}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                {
                                    profileData.login_type === 'S' && (
                                        <div className="col-12">
                                            {/* Create Password Input */}
                                            <div className="changeInput-box single-input-inner">
                                                <div className="loginPasswordInputBox input-group">
                                                    <input name="password" type={showPassword ? "text" : "password"} id="CreatePassword" className="form-control" placeholder={t('enterFieldName', { field: t("Password") })} autoComplete="off" {...register('password')} />
                                                    <span className="input-group-text" id="CreatetogglePassword" onClick={() => setShowPassword(prev => !prev)} style={{ cursor: "pointer" }}>
                                                        <img
                                                            src={showPassword ? process.env.PUBLIC_URL + '/assets/img/icon/open-eye.svg' : process.env.PUBLIC_URL + '/assets/img/icon/close-eye.svg'}
                                                            alt={showPassword ? "Close-eye" : "Open-eye"}
                                                            className={showPassword ? "CloseEye" : "OpenEye"}
                                                        />
                                                    </span>
                                                </div>
                                                <p className="text-danger">
                                                    {errors.password?.message}
                                                </p>
                                            </div>

                                        </div>
                                    )
                                }
                                <div className="col-12">
                                    <div className="single-checkbox-inner">
                                        <div className='row'>
                                            <div className="col-auto pr-0">
                                                <input name="terms_and_conditions" type="checkbox" id="one" {...register('terms_and_conditions')} className='m-1'
                                                />

                                            </div>
                                            <div className="col m-0 p-0">
                                                <label htmlFor="one" className='mx-3'>
                                                    {t('agreeTo')} <Link to="/tandc" className='text-primary'>{t('TnC')}</Link> {t('and')} <Link to="/privacy-policy" className='text-primary'>{t('privacyPolicy')}</Link> {t('proceedFurther')}
                                                </label>

                                            </div>
                                        </div>
                                        <p className="text-danger">
                                            {errors.terms_and_conditions?.message}
                                        </p>
                                    </div>
                                </div>
                                <div className="col-12 mb-4">
                                    <button className="btn btn-base w-100" type="submit">
                                        {t('createAcc')}
                                    </button>
                                </div>
                                <div className="col-12">
                                    <span className='m-1'>{t('accAlready')}</span>
                                    <Link to="/sign-in"><strong>{t('signIn')}</strong></Link>
                                </div>
                                <div className='social-btns mt-3'>
                                    <div className='d-flex justify-content-center gap-5'>
                                        <div className='google-btn'>
                                            <button className="btn-social btn-google" type='button' onClick={handleGoogleSignIn}>
                                                <i className="fa fa-google"></i>
                                            </button>
                                        </div>
                                        <div className='facebook-btn'>
                                            <button className="btn-social btn-facebook" type='button' onClick={handleFacebookSignIn}>
                                                <i className="fa fa-facebook"></i>
                                            </button>
                                        </div>
                                        <div className='apple-btn'>
                                            <button className="btn-social btn-apple" type='button' onClick={handleAppleSignIn}>
                                                <i className="fa fa-apple"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div >
    )
}

export default Signup;